export const getVisualLevel = (node) => Number(node?.level || 0);

const LEGACY_TO_NEW_STATUS = {
  BELOW: 'FAST',
  OPTIMAL: 'MEDIUM',
  ABOVE: 'SLOW',
};

const SEMAPHORE_PRIORITY = {
  SLOW: 3,
  MEDIUM: 2,
  FAST: 1,
};

export const SEMAPHORE_COLORS = {
  SLOW: { background: '#E53935', border: '#C62828', color: '#FFFFFF' },
  MEDIUM: { background: '#FBC02D', border: '#F9A825', color: '#1F2937' },
  FAST: { background: '#43A047', border: '#2E7D32', color: '#FFFFFF' },
};

export const normalizePerformanceStatus = (value) => {
  if (typeof value !== 'string') return null;
  if (LEGACY_TO_NEW_STATUS[value]) return LEGACY_TO_NEW_STATUS[value];
  if (value === 'FAST' || value === 'MEDIUM' || value === 'SLOW') return value;
  return null;
};

export const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const toValidDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const getNodeDurationMinutes = (node, nowMs = Date.now()) => {
  const openedAt = toValidDate(node?.openedAt);
  if (!openedAt) return null;

  const closedAt = toValidDate(node?.closedAt) || new Date(nowMs);
  const durationMinutes = (closedAt.getTime() - openedAt.getTime()) / 1000 / 60;

  if (!Number.isFinite(durationMinutes) || durationMinutes < 0) return null;
  return durationMinutes;
};

export const getOpenProcessStatus = (node, nowMs = Date.now()) => {
  if (node?.closedAt) return null;

  const openedAt = toValidDate(node?.openedAt);
  if (!openedAt) return null;

  const elapsedMinutes = (nowMs - openedAt.getTime()) / 1000 / 60;
  if (!Number.isFinite(elapsedMinutes)) return null;

  const lowThreshold = toFiniteNumber(node?.lowTimeThreshold);
  const highThreshold = toFiniteNumber(node?.highTimeThreshold);

  if (lowThreshold !== null && elapsedMinutes < lowThreshold) return 'FAST';
  if (highThreshold !== null && elapsedMinutes > highThreshold) return 'SLOW';
  if (lowThreshold !== null || highThreshold !== null) return 'MEDIUM';

  return null;
};

export const buildRowIndexByProcessId = (nodes = [], edges = []) => {
  const nodesByLevel = new Map();
  const nodeByProcessId = new Map();

  nodes.forEach((node) => {
    const level = getVisualLevel(node);
    if (!nodesByLevel.has(level)) nodesByLevel.set(level, []);
    nodesByLevel.get(level).push(node);
    nodeByProcessId.set(node.processId, node);
  });

  const sortedLevels = Array.from(nodesByLevel.keys()).sort((a, b) => a - b);
  const rowIndexByProcessId = new Map();

  sortedLevels.forEach((level) => {
    const currentLevelNodes = nodesByLevel.get(level) || [];

    const rankedNodes = currentLevelNodes.map((node) => {
      const parentRows = edges
        .filter((edge) => edge.toProcessId === node.processId)
        .map((edge) => edge.fromProcessId)
        .filter((parentId) => {
          const parentNode = nodeByProcessId.get(parentId);
          if (!parentNode) return false;
          return getVisualLevel(parentNode) < level;
        })
        .map((parentId) => rowIndexByProcessId.get(parentId))
        .filter((rowIndex) => Number.isFinite(rowIndex));

      const score = parentRows.length
        ? parentRows.reduce((sum, rowIndex) => sum + rowIndex, 0) / parentRows.length
        : Number.POSITIVE_INFINITY;

      return { node, score };
    });

    rankedNodes.sort((a, b) => {
      if (a.score !== b.score) return a.score - b.score;
      return a.node.processId - b.node.processId;
    });

    const assignedRows = [];

    rankedNodes.forEach((item, index) => {
      const hasFiniteScore = Number.isFinite(item.score);
      let row = hasFiniteScore ? item.score : index;
      const overlapsWithAssignedRows = (candidateRow) =>
        assignedRows.some((assignedRow) => Math.abs(assignedRow - candidateRow) < 1);

      while (overlapsWithAssignedRows(row)) {
        row += 1;
      }

      assignedRows.push(row);
      rowIndexByProcessId.set(item.node.processId, row);
    });
  });

  return rowIndexByProcessId;
};

export const buildPerformanceByOriginProcess = (edges = []) => {
  const statusByProcessId = new Map();

  edges.forEach((edge) => {
    const status = normalizePerformanceStatus(edge?.metadata?.performanceStatus);
    if (!status || !SEMAPHORE_PRIORITY[status]) return;

    const current = statusByProcessId.get(edge.fromProcessId);
    if (!current || SEMAPHORE_PRIORITY[status] > SEMAPHORE_PRIORITY[current]) {
      statusByProcessId.set(edge.fromProcessId, status);
    }
  });

  return statusByProcessId;
};

export const getCriticalPathMinutes = (nodes = [], edges = [], nowMs = Date.now()) => {
  if (!Array.isArray(nodes) || !nodes.length) return 0;

  const durationByProcessId = new Map(
    nodes.map((node) => [node.processId, getNodeDurationMinutes(node, nowMs) || 0]),
  );

  const outgoingByProcessId = new Map();
  const incomingCountByProcessId = new Map();

  nodes.forEach((node) => {
    outgoingByProcessId.set(node.processId, []);
    incomingCountByProcessId.set(node.processId, 0);
  });

  edges.forEach((edge) => {
    if (
      !Number.isFinite(edge?.fromProcessId) ||
      !Number.isFinite(edge?.toProcessId) ||
      edge.fromProcessId === edge.toProcessId
    ) {
      return;
    }

    if (!outgoingByProcessId.has(edge.fromProcessId)) {
      outgoingByProcessId.set(edge.fromProcessId, []);
    }

    outgoingByProcessId.get(edge.fromProcessId).push(edge.toProcessId);

    if (!incomingCountByProcessId.has(edge.toProcessId)) {
      incomingCountByProcessId.set(edge.toProcessId, 0);
    }

    incomingCountByProcessId.set(
      edge.toProcessId,
      (incomingCountByProcessId.get(edge.toProcessId) || 0) + 1,
    );
  });

  const memo = new Map();
  const visiting = new Set();

  const dfs = (processId) => {
    if (memo.has(processId)) return memo.get(processId);
    if (visiting.has(processId)) {
      return durationByProcessId.get(processId) || 0;
    }

    visiting.add(processId);

    const ownDuration = durationByProcessId.get(processId) || 0;
    const children = outgoingByProcessId.get(processId) || [];

    const maxChildDuration = children.reduce((maxDuration, childProcessId) => {
      const childDuration = dfs(childProcessId);
      return Math.max(maxDuration, childDuration);
    }, 0);

    const totalDuration = ownDuration + maxChildDuration;

    visiting.delete(processId);
    memo.set(processId, totalDuration);

    return totalDuration;
  };

  const rootProcessIds = nodes
    .map((node) => node.processId)
    .filter((processId) => (incomingCountByProcessId.get(processId) || 0) === 0);

  const candidateProcessIds = rootProcessIds.length
    ? rootProcessIds
    : nodes.map((node) => node.processId);

  return candidateProcessIds.reduce((maxDuration, processId) => {
    const totalDuration = dfs(processId);
    return Math.max(maxDuration, totalDuration);
  }, 0);
};
